import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UNAUTHORIZED_EVENT, api, type StudioUser } from './api.ts';
import { CouponDrawer } from './pages/CouponDrawer.tsx';
import { CouponCreate } from './pages/CouponCreate.tsx';
import { Coupons } from './pages/Coupons.tsx';
import { Inbox } from './pages/Inbox.tsx';
import { Login, VerifyLogin } from './pages/Login.tsx';
import { MerchantWorkspace } from './pages/MerchantWorkspace.tsx';
import { Merchants } from './pages/Merchants.tsx';
import { Review } from './pages/Review.tsx';
import { Sites } from './pages/Sites.tsx';
import { AFFILIATE_SHORTCUTS, affiliatePage } from './pages/affiliate/routes.tsx';
import { CATALOG_SHORTCUTS, catalogPage } from './pages/catalog/routes.tsx';
import { SOURCES_SHORTCUTS, sourcesPage } from './pages/sources/routes.tsx';
import { TENANCY_SHORTCUTS, tenancyPage } from './pages/tenancy/routes.tsx';
import { INBOX_SHORTCUTS, inboxPage } from './pages/inbox/routes.tsx';
import { PLATFORM_SHORTCUTS, platformPage } from './pages/platform/routes.tsx';
import { REVENUE_SHORTCUTS, revenuePage } from './pages/revenue/routes.tsx';
import { SPONSORED_SHORTCUTS, sponsoredPage } from './pages/sponsored/routes.tsx';
import { COMPANION_SHORTCUTS, CompanionPanel } from './companion/CompanionPanel.tsx';
import { Link, matchPath, useRouter } from './router.tsx';
import { isTyping } from './ui/keys.ts';
import { navItems, readCollapsed, visibleNav, writeCollapsed } from './nav.ts';
import { CONTENT_PAGE_SHORTCUTS, contentPage } from './pages/content/routes.tsx';
import { BrandDeals } from './pages/BrandDeals.tsx';
import { AddCoupons } from './pages/intake/AddCoupons.tsx';
import { Coverage } from './pages/intake/Coverage.tsx';
import { Kbd, Loading, Modal } from './ui/kit.tsx';
import { useChanges } from './ui/changes.ts';

const SHORTCUTS: [string, string][] = [
  ['g then t / r / m / c / s', 'Go to Today, Review, Merchants, Coupons, Sites (only the pages your roles allow)'],
  ['/', 'Search on the current list'],
  ['j / k', 'Next / previous row'],
  ['Enter or o', 'Open the selected row'],
  ['a', 'Review: approve'],
  ['r', 'Review: reject (then 1–7 picks a reason)'],
  ['x', 'Review: select row for a batch'],
  ['shift + a', 'Review: approve selected rows'],
  ...AFFILIATE_SHORTCUTS,
  ['Esc', 'Close drawer or dialog'],
  ['?', 'Show this help'],
  ...CATALOG_SHORTCUTS,
  ...TENANCY_SHORTCUTS,
  ...INBOX_SHORTCUTS,
  ...PLATFORM_SHORTCUTS,
  ...SOURCES_SHORTCUTS,
  ...REVENUE_SHORTCUTS,
  ...SPONSORED_SHORTCUTS,
  ...CONTENT_PAGE_SHORTCUTS,
  ...COMPANION_SHORTCUTS,
];

export function App() {
  const { path, navigate } = useRouter();
  const [user, setUser] = useState<StudioUser | null | undefined>(undefined);

  useEffect(() => {
    api
      .session()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null));
    const onUnauthorized = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  if (path === '/login/verify') return <VerifyLogin onSignedIn={(u) => { setUser(u); navigate('/', { replace: true }); }} />;
  if (user === undefined) return <Loading label="Starting Studio" />;
  if (user === null) return <Login />;
  return <Shell user={user} onSignedOut={() => setUser(null)} />;
}

function Shell({ user, onSignedOut }: { user: StudioUser; onSignedOut: () => void }) {
  const { path, query, navigate, setQuery } = useRouter();
  const [help, setHelp] = useState(false);
  const [companion, setCompanion] = useState(false);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const changes = useChanges();
  const goPrefix = useRef(0);
  const groups = useMemo(() => visibleNav(user.capabilities), [user.capabilities]);
  const nav = useMemo(() => navItems(groups).filter((item) => item.key && !item.placeholder), [groups]);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);
  const canReview = user.capabilities.includes('content.review:read');
  const toggleGroup = (id: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeCollapsed(next);
      return next;
    });

  useEffect(() => {
    if (!canReview) return;
    api
      .op<{ total: number }>('admin.review.queue', { limit: 1 })
      .then((r) => setPendingCount(r.total))
      .catch(() => setPendingCount(null));
  }, [changes, canReview]);

  const onKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      // The companion handles its own keys (grid shortcuts) and closes with Esc.
      if (event.target instanceof Element && event.target.closest('[data-companion]')) return;
      if (event.key === '.') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setCompanion(true);
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        setHelp((h) => !h);
        return;
      }
      if (event.key === '/') {
        const search = document.querySelector<HTMLInputElement>('[data-search]');
        if (search) {
          event.preventDefault();
          search.focus();
          search.select();
        }
        return;
      }
      if (event.key === 'g') {
        goPrefix.current = Date.now();
        return;
      }
      if (Date.now() - goPrefix.current < 1200) {
        const target = nav.find((n) => n.key === event.key);
        goPrefix.current = 0;
        if (target) {
          event.preventDefault();
          event.stopImmediatePropagation();
          navigate(target.to);
        }
      }
    },
    [navigate, nav],
  );
  useEffect(() => {
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onKey]);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    onSignedOut();
  };

  const merchant = matchPath('/merchants/:ref', path);
  let page;
  if (path === '/') page = <Inbox user={user} groups={groups} />;
  else if (path === '/brand-deals') page = <BrandDeals />;
  else if (path === '/review') page = <Review />;
  else if (path === '/merchants') page = <Merchants />;
  else if (merchant) page = <MerchantWorkspace merchantRef={merchant['ref']!} />;
  else if (path === '/coupons') page = <Coupons />;
  else if (path === '/coupons/new') page = <CouponCreate />;
  else if (path === '/add-coupons') page = <AddCoupons />;
  else if (path === '/coverage') page = <Coverage />;
  else if (path === '/sites') page = <Sites />;
  else page = contentPage(path) ?? catalogPage(path) ?? affiliatePage(path) ?? tenancyPage(path) ?? revenuePage(path) ?? sourcesPage(path) ?? sponsoredPage(path) ?? platformPage(path) ?? inboxPage(path) ?? <p className="empty">Page not found. <Link to="/">Back to Today</Link></p>;

  const couponRef = query.get('coupon');
  const isActive = (to: string) => (to === '/' ? path === '/' : path.startsWith(to) || (to === '/merchants' && path === '/global-merchants'));

  return (
    <div className="shell">
      <a className="skip" href="#main">
        Skip to content
      </a>
      <nav className="sidebar" aria-label="Studio">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            jc
          </span>
          <span>
            Jetscale Coupons <small>Studio</small>
          </span>
        </div>
        <div className="nav-groups">
          {groups.map((group) => {
            const open = !group.label || !collapsed.has(group.id) || group.items.some((item) => isActive(item.to));
            return (
              <div key={group.id} className="nav-group">
                {group.label ? (
                  <button type="button" className="nav-heading" aria-expanded={open} aria-controls={`nav-${group.id}`} onClick={() => toggleGroup(group.id)}>
                    <span>{group.label}</span>
                    <span aria-hidden="true" className="nav-caret">{open ? '▾' : '▸'}</span>
                  </button>
                ) : null}
                {open ? (
                  <ul id={`nav-${group.id}`}>
                    {group.items.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          className={[isActive(item.to) ? 'active' : '', item.placeholder ? 'placeholder' : ''].filter(Boolean).join(' ') || undefined}
                          aria-current={isActive(item.to) ? 'page' : undefined}
                        >
                          <span>{item.label}</span>
                          {item.to === '/review' && pendingCount ? (
                            <span className="count">{pendingCount}</span>
                          ) : item.placeholder ? (
                            <small className="muted">{item.placeholder}</small>
                          ) : item.key ? (
                            <Kbd>g {item.key}</Kbd>
                          ) : null}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            );
          })}
        </div>
        <button type="button" className="companion-launch" onClick={() => setCompanion(true)}>
          <span>
            <strong>Companion</strong>
            <small>Ask, review the grid, save.</small>
          </span>
          <Kbd>.</Kbd>
        </button>
        <div className="sidebar-foot">
          <span className="who" title={user.memberships.map((m) => `${m.roleNames} · ${m.property?.name ?? m.brand?.name ?? m.organization.name}`).join('\n')}>
            {user.name}
            <small>{user.email}</small>
          </span>
          <button type="button" className="btn btn-ghost btn-small" onClick={signOut}>
            Sign out
          </button>
          <button type="button" className="btn btn-ghost btn-small" onClick={() => setHelp(true)} aria-label="Keyboard shortcuts">
            ?
          </button>
        </div>
      </nav>
      <main id="main" className="main">
        {page}
      </main>
      {companion ? <CompanionPanel onClose={() => setCompanion(false)} /> : null}
      {couponRef ? <CouponDrawer couponRef={couponRef} onClose={() => setQuery({ coupon: null })} /> : null}
      {help ? (
        <Modal
          title="Keyboard shortcuts"
          onClose={() => setHelp(false)}
          actions={
            <button type="button" className="btn" onClick={() => setHelp(false)}>
              Close
            </button>
          }
        >
          <table className="shortcuts">
            <tbody>
              {SHORTCUTS.map(([keys, action]) => (
                <tr key={keys}>
                  <td>
                    <Kbd>{keys}</Kbd>
                  </td>
                  <td>{action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      ) : null}
    </div>
  );
}
